// src/pages/dashboard/FollowUpsPage.tsx
import React, { useState } from "react";
import {
  BellRing,
  Loader2,
  CheckCircle2,
  Clock,
  Phone,
  StickyNote,
  Mail,
  CalendarClock,
  Activity,
  Trash2,
  ExternalLink,
} from "lucide-react";
import {
  useGetFollowUpsQuery,
  useUpdateFollowUpMutation,
  useDeleteFollowUpMutation,
  FollowUp,
} from "../../app/api/adminApi";
import ContactWorkspace from "../../components/dashboard/ContactWorkspace";

function cn(...c: Array<string | false | null | undefined>) {
  return c.filter(Boolean).join(" ");
}
const fmtDate = (d?: string | null) =>
  d
    ? new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(d))
    : "—";

const typeIcon = (t: string) => {
  switch (t) {
    case "call":
      return <Phone className="h-4 w-4 text-emerald-500" />;
    case "email":
      return <Mail className="h-4 w-4 text-violet-500" />;
    case "task":
      return <CalendarClock className="h-4 w-4 text-amber-500" />;
    case "status_change":
      return <Activity className="h-4 w-4 text-indigo-500" />;
    default:
      return <StickyNote className="h-4 w-4 text-slate-400" />;
  }
};

const FILTERS = [
  { key: "upcoming", label: "Tasks & reminders" },
  { key: "open", label: "Open" },
  { key: "done", label: "Completed" },
  { key: "", label: "All activity" },
];

const FollowUpsPage: React.FC = () => {
  const [filter, setFilter] = useState("upcoming");
  const [active, setActive] = useState<{ email: string; name?: string } | null>(
    null
  );

  const { data, isLoading } = useGetFollowUpsQuery({
    status: filter || undefined,
    limit: 100,
  });
  const [updateFollowUp] = useUpdateFollowUpMutation();
  const [deleteFollowUp] = useDeleteFollowUpMutation();

  const items = data?.data?.followups || [];

  const isOverdue = (f: FollowUp) =>
    f.type === "task" && !f.completed && f.dueAt && new Date(f.dueAt) < new Date();

  return (
    <div>
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-900">
          <BellRing className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">
            Follow-ups
          </h1>
          <p className="text-sm text-slate-500">
            Every note, call, task and email logged against your contacts.
          </p>
        </div>
      </div>

      <div className="mb-5 flex flex-wrap gap-1.5">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={cn(
              "rounded-xl border px-3.5 py-1.5 text-xs font-semibold transition",
              filter === f.key
                ? "border-slate-900 bg-slate-900 text-white"
                : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20 text-slate-400">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      ) : items.length ? (
        <div className="space-y-2.5">
          {items.map((f) => (
            <div
              key={f._id}
              className={cn(
                "flex items-start gap-3 rounded-2xl border bg-white p-4 shadow-sm",
                isOverdue(f) ? "border-red-200" : "border-slate-200"
              )}
            >
              <div className="mt-0.5">{typeIcon(f.type)}</div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    onClick={() =>
                      setActive({ email: f.contactEmail, name: f.contactName })
                    }
                    className="flex items-center gap-1 text-sm font-semibold text-slate-800 hover:text-violet-600"
                  >
                    {f.contactName || f.contactEmail}
                    <ExternalLink className="h-3 w-3 opacity-50" />
                  </button>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold capitalize text-slate-500">
                    {f.type.replace("_", " ")}
                  </span>
                  {isOverdue(f) && (
                    <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-600">
                      Overdue
                    </span>
                  )}
                </div>
                {f.subject && (
                  <p className="mt-1 text-sm font-medium text-slate-700">
                    {f.subject}
                  </p>
                )}
                {f.message && (
                  <p className="mt-0.5 whitespace-pre-wrap text-xs text-slate-600">
                    {f.message}
                  </p>
                )}
                <p className="mt-1 text-[11px] text-slate-400">
                  {f.createdByName ? `${f.createdByName} · ` : ""}
                  {fmtDate(f.createdAt)}
                  {f.dueAt ? ` · due ${fmtDate(f.dueAt)}` : ""}
                </p>
              </div>

              <div className="flex shrink-0 items-center gap-1.5">
                {f.type === "task" && (
                  <button
                    onClick={() =>
                      updateFollowUp({ id: f._id, completed: !f.completed })
                    }
                    className={cn(
                      "flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-semibold",
                      f.completed
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-amber-100 text-amber-700"
                    )}
                  >
                    {f.completed ? (
                      <CheckCircle2 className="h-3.5 w-3.5" />
                    ) : (
                      <Clock className="h-3.5 w-3.5" />
                    )}
                    {f.completed ? "Done" : "Mark done"}
                  </button>
                )}
                <button
                  onClick={() => {
                    if (window.confirm("Delete this activity?"))
                      deleteFollowUp({ id: f._id });
                  }}
                  className="rounded-lg border border-slate-200 p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-500"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-slate-400">
          <BellRing className="h-10 w-10 text-slate-300" />
          <p className="text-sm font-medium text-slate-600">Nothing here yet</p>
          <p className="text-xs">
            Open a contact and log a note, call or task to see it here.
          </p>
        </div>
      )}

      {active && (
        <ContactWorkspace
          email={active.email}
          name={active.name}
          onClose={() => setActive(null)}
        />
      )}
    </div>
  );
};

export default FollowUpsPage;
